export interface Rng {
  /** Returns x in [0, 1). */
  next(): number;
  /** Returns x in [min, max). */
  range(min: number, max: number): number;
  /** Returns integer in [min, max). */
  intRange(min: number, max: number): number;
  /** Returns a random element of arr. Throws on empty arrays. */
  pick<T>(arr: readonly T[]): T;
  /** Returns true with probability p (default 0.5). */
  bool(p?: number): boolean;
  /** Returns the seed that initialised this generator. */
  seed(): number;
}

// SplitMix32: small, fast, decent quality for game randomness.
function splitmix32(initial: number): () => number {
  let s = initial | 0;
  return () => {
    s = (s + 0x9e3779b9) | 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x85ebca6b);
    z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35);
    return ((z ^ (z >>> 16)) >>> 0) / 4294967296;
  };
}

export function createRng(seed?: number): Rng {
  const usedSeed = seed ?? (Date.now() ^ (Math.random() * 0x100000000)) | 0;
  const next = splitmix32(usedSeed);
  return {
    next,
    range(min, max) {
      return min + next() * (max - min);
    },
    intRange(min, max) {
      return Math.floor(min + next() * (max - min));
    },
    pick<T>(arr: readonly T[]): T {
      if (arr.length === 0) throw new Error('rng.pick: empty array');
      return arr[Math.floor(next() * arr.length)] as T;
    },
    bool(p = 0.5) {
      return next() < p;
    },
    seed() {
      return usedSeed;
    },
  };
}
